/**
 * Isi Kebijakan Privasi halaman publik, dwibahasa.
 *
 * PENTING: naskah ini RANCANGAN. Ditulis menyesuaikan kenyataan teknis halaman
 * publik (tidak ada login, tidak ada formulir, tidak ada pelacakan pihak
 * ketiga), tetapi belum ditinjau Fungsi Legal & Compliance. Bagian yang masih
 * berupa contoh ditandai dengan kurung siku, mis. [alamat email], dan wajib
 * diisi/diperiksa sebelum halaman ditayangkan.
 */

export const PRIVACY_LAST_UPDATED = '8 September 2026';
export const PRIVACY_LAST_UPDATED_EN = '8 September 2026';

/**
 * Menampilkan peringatan "naskah masih rancangan" di atas isi kebijakan.
 *
 * Sengaja TERPISAH dari penanda mockup (`IS_MOCKUP`): yang satu soal data
 * contoh, yang ini soal naskah hukum yang belum ditinjau. Set ke false hanya
 * setelah Fungsi Legal & Compliance menyetujui isinya dan seluruh isian dalam
 * kurung siku sudah dilengkapi.
 */
export const PRIVACY_IS_DRAFT = false;

const privacyPolicyContent = {
  id: [
    {
      heading: 'Ruang Lingkup',
      paragraphs: [
        'Kebijakan Privasi ini berlaku untuk halaman Pengumuman Tender publik pada aplikasi Digital Procurement HYDRO milik PT Pertamina (Persero). Halaman publik adalah halaman yang dapat diakses tanpa masuk (login).',
        'Kebijakan ini tidak mengatur bagian aplikasi yang memerlukan akun, hubungan kontraktual dengan mitra, maupun proses pengadaan itu sendiri. Ketentuan untuk bagian tersebut diatur terpisah.',
      ],
    },
    {
      heading: 'Informasi yang Kami Kumpulkan',
      paragraphs: [
        'Halaman publik ini tidak meminta Anda membuat akun, mengisi formulir, atau menyerahkan data pribadi apa pun. Kami tidak mengumpulkan nama, alamat surel, nomor telepon, maupun identitas Anda dari halaman ini.',
        'Seperti umumnya layanan web, server dapat mencatat data teknis secara otomatis untuk keperluan keamanan dan pemeliharaan, yaitu:',
      ],
      bullets: [
        'alamat IP dan informasi peramban (jenis dan versi);',
        'halaman yang diakses beserta waktu aksesnya;',
        'kode status dan ukuran respons.',
      ],
    },
    {
      heading: 'Cookie dan Penyimpanan Lokal',
      paragraphs: [
        'Halaman publik ini tidak menggunakan cookie untuk periklanan, profiling, maupun pelacakan lintas situs.',
        'Kami hanya menyimpan preferensi bahasa Anda pada penyimpanan lokal peramban (localStorage). Data ini tersimpan di perangkat Anda sendiri, tidak dikirim ke server kami, dan dapat Anda hapus kapan saja melalui pengaturan peramban.',
      ],
    },
    {
      heading: 'Penggunaan Informasi',
      paragraphs: ['Data teknis di atas hanya digunakan untuk:'],
      bullets: [
        'menjaga keamanan dan ketersediaan layanan;',
        'mendeteksi dan menangani gangguan atau penyalahgunaan;',
        'menyusun statistik agregat kunjungan yang tidak mengidentifikasi individu.',
      ],
    },
    {
      heading: 'Pembagian Informasi',
      paragraphs: [
        'Kami tidak menjual, menyewakan, atau memperdagangkan data kunjungan Anda kepada pihak mana pun.',
        'Data hanya dapat dibagikan kepada penyedia infrastruktur yang mengoperasikan layanan atas nama kami, dan kepada aparat berwenang apabila diwajibkan oleh peraturan perundang-undangan yang berlaku.',
      ],
    },
    {
      heading: 'Penyimpanan Data',
      paragraphs: [
        'Catatan teknis (log) disimpan selama [jangka waktu penyimpanan log — harap dikonfirmasi Fungsi IT] dan dihapus atau dianonimkan setelahnya, kecuali sedang diperlukan untuk penanganan insiden keamanan atau pemenuhan kewajiban hukum.',
      ],
    },
    {
      heading: 'Keamanan',
      paragraphs: [
        'Kami menerapkan langkah pengamanan teknis dan organisasi yang wajar untuk melindungi layanan ini. Namun perlu dipahami bahwa tidak ada metode transmisi maupun penyimpanan elektronik yang sepenuhnya bebas risiko.',
      ],
    },
    {
      heading: 'Hak Anda',
      paragraphs: [
        'Sepanjang data yang kami proses tergolong data pribadi menurut Undang-Undang Nomor 27 Tahun 2022 tentang Pelindungan Data Pribadi, Anda memiliki hak untuk memperoleh informasi, meminta akses, meminta perbaikan, serta meminta penghapusan data tersebut, sesuai ketentuan yang berlaku.',
        'Permohonan dapat disampaikan melalui kontak pada bagian akhir kebijakan ini.',
      ],
    },
    {
      heading: 'Informasi Pengadaan yang Ditampilkan',
      paragraphs: [
        'Pengumuman pada halaman ini bersifat informasi umum. Keikutsertaan dalam proses pengadaan hanya dapat dilakukan oleh mitra yang memperoleh undangan resmi melalui mekanisme yang berlaku di Pertamina.',
        'Halaman ini tidak menampilkan nilai pengadaan, harga perkiraan, maupun informasi komersial lain yang bersifat rahasia.',
      ],
    },
    {
      heading: 'Tautan ke Situs Lain',
      paragraphs: [
        'Halaman ini dapat memuat tautan ke situs pihak lain. Kebijakan Privasi ini tidak berlaku bagi situs tersebut, dan kami tidak bertanggung jawab atas praktik privasi mereka.',
      ],
    },
    {
      heading: 'Perubahan Kebijakan',
      paragraphs: [
        'Kebijakan Privasi ini dapat diperbarui sewaktu-waktu. Tanggal pembaruan terakhir dicantumkan pada bagian atas halaman. Perubahan berlaku sejak dimuat pada halaman ini.',
      ],
    },
    {
      heading: 'Kontak',
      paragraphs: [
        'Pertanyaan mengenai Kebijakan Privasi ini dapat disampaikan kepada [nama fungsi/unit penanggung jawab] melalui surel [alamat surel] atau alamat [alamat kantor].',
      ],
    },
  ],

  en: [
    {
      heading: 'Scope',
      paragraphs: [
        'This Privacy Policy applies to the public Tender Announcement pages of the Digital Procurement HYDRO application operated by PT Pertamina (Persero). Public pages are those accessible without signing in.',
        'It does not govern the parts of the application that require an account, contractual relationships with suppliers, or the procurement process itself. Those are covered by separate terms.',
      ],
    },
    {
      heading: 'Information We Collect',
      paragraphs: [
        'These public pages do not ask you to create an account, complete a form, or submit any personal data. We do not collect your name, email address, phone number, or identity from these pages.',
        'As with most web services, our servers may automatically record technical data for security and maintenance purposes, namely:',
      ],
      bullets: [
        'IP address and browser information (type and version);',
        'pages accessed and the time of access;',
        'response status codes and sizes.',
      ],
    },
    {
      heading: 'Cookies and Local Storage',
      paragraphs: [
        'These public pages do not use cookies for advertising, profiling, or cross-site tracking.',
        'We only store your language preference in your browser local storage. That value stays on your own device, is never sent to our servers, and can be cleared at any time through your browser settings.',
      ],
    },
    {
      heading: 'How We Use Information',
      paragraphs: ['The technical data above is used solely to:'],
      bullets: [
        'maintain the security and availability of the service;',
        'detect and respond to disruption or misuse;',
        'produce aggregate visit statistics that do not identify individuals.',
      ],
    },
    {
      heading: 'Sharing of Information',
      paragraphs: [
        'We do not sell, rent, or trade your visit data to anyone.',
        'Data may only be shared with infrastructure providers operating the service on our behalf, and with competent authorities where required by applicable law.',
      ],
    },
    {
      heading: 'Data Retention',
      paragraphs: [
        'Technical logs are retained for [log retention period — to be confirmed by the IT function] and are then deleted or anonymised, unless still required for security incident handling or to meet legal obligations.',
      ],
    },
    {
      heading: 'Security',
      paragraphs: [
        'We apply reasonable technical and organisational safeguards to protect this service. Please note, however, that no method of electronic transmission or storage is entirely free of risk.',
      ],
    },
    {
      heading: 'Your Rights',
      paragraphs: [
        'To the extent the data we process qualifies as personal data under Law No. 27 of 2022 on Personal Data Protection, you have the right to be informed, to request access, to request correction, and to request erasure of that data, subject to applicable provisions.',
        'Requests may be submitted through the contact details at the end of this policy.',
      ],
    },
    {
      heading: 'Procurement Information Displayed',
      paragraphs: [
        'Announcements on these pages are general information only. Participation in a procurement process is open only to suppliers who receive an official invitation through the mechanisms applicable at Pertamina.',
        'These pages do not display procurement values, owner estimates, or any other confidential commercial information.',
      ],
    },
    {
      heading: 'Links to Other Sites',
      paragraphs: [
        'These pages may contain links to third-party websites. This Privacy Policy does not apply to those sites, and we are not responsible for their privacy practices.',
      ],
    },
    {
      heading: 'Changes to This Policy',
      paragraphs: [
        'This Privacy Policy may be updated from time to time. The date of the latest update is shown at the top of this page. Changes take effect once published here.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: [
        'Questions about this Privacy Policy may be directed to [responsible function/unit] at [email address] or [office address].',
      ],
    },
  ],
};

export default privacyPolicyContent;
